// src/lib/extract.js — recursive blob-ref extractor (pure, no React)
//
// Walks a record value generically and collects every blob reference.
// Never hardcodes paths: blobs live at any depth (embed.images[].image,
// embed.video.video, embed.video.captions[].file, embed.external.thumb,
// embed.media.* in recordWithMedia, avatar, banner, and arbitrary positions
// in third-party lexicons).
//
// Canonical fixture shapes (single source of truth — §3b of the plan):
//   F1 — modern            { "$type": "blob", "ref": { "$link": "bafk..." }, "mimeType": "...", "size": 123 }
//   F2 — modern, hydrated  { "$type": "blob", "ref": "bafk..." }
//   F3 — legacy            { "cid": "bafk...", "mimeType": "image/jpeg" }
//   F4 — deeply nested inside app.bsky embeds
//   F5 — arbitrary position inside a third-party lexicon record
//
// REAL-WORLD (learned from production): the @atproto/api client hydrates
// `ref` values into multiformats CID objects ({code, version, hash, ...})
// when it parses listRecords responses. `CID.toString()` yields the
// "bafk..." string. The hydrated blob node is wrapped as
// { ref: CID, mimeType, size, original: { $type: 'blob', ... } } and the
// wrapper itself has NO $type — so we must also recognize objects whose
// `ref` resolves to a CID AND that carry mimeType/size.
//
// NOTE on types: this module's whole job IS parsing the API boundary (raw
// JSON + the client's hydration). The branch points below test the SHAPE
// that carries a CID ($link / toString / bare string) rather than the
// runtime type where possible.

const CID_RE = /^baf[a-z0-9]+$/i;
const isCidLike = (s) => CID_RE.test(String(s));

/** Resolve a blob ref (string, {$link}, or hydrated CID object) to a CID string or null. */
function cidFromRef(ref) {
  if (ref === null || ref === undefined) return null;

  // {$link} wrapper (F1)
  if (ref.$link !== undefined) return isCidLike(ref.$link) ? ref.$link : null;

  // hydrated CID object (F2) — has its own toString; String() renders bafk...
  if (ref.toString !== undefined && ref.toString !== Object.prototype.toString) {
    const s = String(ref);

    return isCidLike(s) ? s : null;
  }

  // bare string (F2 plain / tests)
  if (isCidLike(ref)) return ref;

  return null;
}

/**
 * @param {*} value - a record value (object, array, primitive)
 * @returns {Array<{cid: string, mimeType?: string, size?: number}>} unique blob refs by cid
 */
export function extractBlobRefs(value) {
  const seen = new Set();
  const out = [];

  const push = (cid, mimeType, size) => {
    if (seen.has(cid)) return;
    seen.add(cid);
    const entry = { cid };

    if (mimeType !== undefined && mimeType !== null && mimeType !== '') entry.mimeType = String(mimeType);

    if (size !== undefined && size !== null && Number.isFinite(Number(size))) entry.size = Number(size);
    out.push(entry);
  };

  const walk = (node) => {
    if (node === null || node === undefined) return;

    if (Array.isArray(node)) {
      for (const item of node) walk(item);

      return;
    }

    // The JSON/DOM shape gate itself (primitives carry no blob refs).
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    if (typeof node !== 'object') return;

    const t = node.$type;
    const hasBlobRef = 'ref' in node || 'cid' in node;

    if (t === 'blob') {
      const cid = cidFromRef(node.ref);

      if (cid) {
        push(cid, node.mimeType, node.size);

        return; // do not recurse into ref internals
      }
    } else if (hasBlobRef) {
      // Hydrated wrapper (no $type): { ref: CID, mimeType, size, original } —
      // or legacy F3 { cid, mimeType } — recognize only when it looks like a
      // blob (carries mimeType/size), not arbitrary {ref} fields.
      if (node.ref !== undefined && cidFromRef(node.ref) && ('mimeType' in node || 'size' in node)) {
        push(cidFromRef(node.ref), node.mimeType, node.size);

        return;
      }

      if (
        !t &&
        node.cid !== undefined &&
        node.cid !== null &&
        isCidLike(node.cid) &&
        ('mimeType' in node || 'size' in node)
      ) {
        // F3 legacy shape
        push(node.cid, node.mimeType, node.size);

        return;
      }
    }

    // F4/F5: generic recursion
    for (const key of Object.keys(node)) {
      walk(node[key]);
    }
  };

  walk(value);

  return out;
}

export default extractBlobRefs;